import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Button from 'antd/es/button';
import Progress from 'antd/es/progress';
import Radio from 'antd/es/radio';
import Space from 'antd/es/space';
import Typography from 'antd/es/typography';
import Alert from 'antd/es/alert';
import Spin from 'antd/es/spin';
import Card from 'antd/es/card';
import type { RadioChangeEvent } from 'antd/es/radio';
import message from 'antd/es/message';
import api from '../config/api';
import axios from 'axios';
import type { Question, Answer } from '../types';
import { logErrorToServer } from '../utils/errorLogger';
import { motion, AnimatePresence } from 'framer-motion';

const { Text, Title } = Typography;

// 定义本地Answer类型
interface LocalAnswer {
  [key: number]: number;
}

const Test: React.FC = () => {
  const { testId } = useParams<{ testId: string }>();
  const navigate = useNavigate();
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [answers, setAnswers] = useState<LocalAnswer>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showInstructions, setShowInstructions] = useState(true);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const fetchTestData = async () => {
      if (!testId) {
        setError('测试ID不存在');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // 先读取测试。旧链接的自动恢复发生在这个接口，不能被访问计数阻断。
        const response = await api.get(`/api/tests/scl-90/${testId}`);
        const testData = response.data;

        if (!testData) {
          const errorMsg = '测试数据不存在';
          setError(errorMsg);
          setLoading(false);
          
          // 记录错误日志
          await logErrorToServer(testId, 'TEST_NOT_FOUND', errorMsg, {
            responseStatus: response.status,
            responseData: response.data
          });
          return;
        }

        if (!Array.isArray(testData.questions) || testData.questions.length !== 90) {
          throw new Error('题目数据不完整，请联系管理员');
        }

        if (testData.isCompleted) {
          navigate(`/test/scl-90/${testId}/result`);
          return;
        }

        // 设置题目数据
        setQuestions(testData.questions || []);

        // 如果有已保存的答案，设置答案
        if (testData.answers && testData.answers.length > 0) {
          const savedAnswers: LocalAnswer = {};
          testData.answers.forEach((answer: Answer) => {
            savedAnswers[answer.questionId] = answer.score;
          });
          setAnswers(savedAnswers);
        }

        // 访问计数是辅助统计，失败不能影响用户答题。
        api.post(`/api/tests/${testId}/visit`).catch(() => {
          console.warn('访问次数更新失败，不影响继续答题');
        });

        setLoading(false);
      } catch (error) {
        console.error('获取测试数据失败:', error);
        
        let errorMessage = '获取测试数据失败';
        let errorType = 'OTHER';
        
        if (axios.isAxiosError(error)) {
          if (error.response?.status === 404) {
            errorMessage = '测试不存在';
            errorType = 'TEST_NOT_FOUND';
          } else {
            errorMessage = error.response?.data?.error || '获取测试数据失败';
            errorType = 'OTHER';
          }
        }
        
        setError(errorMessage);
        setLoading(false);
        
        // 记录错误日志
        await logErrorToServer(testId!, errorType, errorMessage, {
          errorResponse: axios.isAxiosError(error) ? {
            status: error.response?.status,
            statusText: error.response?.statusText,
            data: error.response?.data
          } : null,
          errorMessage: error instanceof Error ? error.message : String(error)
        });
      }
    };

    fetchTestData();
  }, [testId]);

  const handleAnswer = (value: number) => {
    if (!questions[currentQuestionIndex] || isAnimating) return;
    
    const currentQuestionId = questions[currentQuestionIndex].id;
    
    // 设置动画状态，防止快速点击
    setIsAnimating(true);
    
    // 先保存答案
    setAnswers(prev => {
      const newAnswers = {
        ...prev,
        [currentQuestionId]: value
      };
      return newAnswers;
    });
    
    // 如果不是最后一题，延迟跳转到下一题
    if (currentQuestionIndex < questions.length - 1) {
      setTimeout(() => {
        setCurrentQuestionIndex(prev => prev + 1);
        // 动画完成后重置状态
        setTimeout(() => {
          setIsAnimating(false);
        }, 200); // 等待动画完成
      }, 200);
    } else {
      // 如果是最后一题，立即重置动画状态
      setTimeout(() => {
        setIsAnimating(false);
      }, 100);
    }
  };

  const handleNext = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    }
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;
    
    try {
      setIsSubmitting(true);
      // 检查是否所有题目都已回答
      const unansweredQuestions = questions.filter(q => answers[q.id] === undefined);
      
      if (unansweredQuestions.length > 0) {
        message.warning('请回答所有题目');
        return;
      }

      // 格式化答案
      const formattedAnswers: Answer[] = questions.map(question => ({
        questionId: question.id,
        score: Number(answers[question.id])
      }));

      // 提交答案
      await api.post(`/api/tests/${testId}/submit`, {
        answers: formattedAnswers
      });

      // 分数统一由服务端依据已保存的 90 道答案计算。
      await api.post(`/api/tests/${testId}/complete`);

      // 跳转到结果页面
      navigate(`/test/scl-90/${testId}/result`);
    } catch (error) {
      console.error('提交测试失败:', error);
      
      let errorMessage = '提交测试失败，请稍后重试';
      let errorType = 'SUBMIT_FAILED';
      
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 404) {
          errorMessage = '测试不存在';
          errorType = 'TEST_NOT_FOUND';
        } else if (error.response?.status === 400) {
          errorMessage = error.response?.data?.error || '提交数据格式错误';
          errorType = 'SUBMIT_FAILED';
        } else {
          errorMessage = error.response?.data?.error || '提交测试失败';
          errorType = 'SUBMIT_FAILED';
        }
      }
      
      // 记录错误日志
      await logErrorToServer(testId!, errorType, errorMessage, {
        currentQuestionIndex,
        totalQuestions: questions.length,
        answeredCount: Object.keys(answers).length,
        unansweredCount: questions.length - Object.keys(answers).length,
        errorResponse: axios.isAxiosError(error) ? {
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data
        } : null,
        errorMessage: error instanceof Error ? error.message : String(error)
      });
      
      message.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (showInstructions && !loading && !error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        {/* 主要内容区域 */}
        <div className="w-full max-w-2xl">
          <div className="bg-white rounded-lg shadow-lg max-h-screen overflow-y-auto">
            {/* 头部区域 */}
            <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white p-6 rounded-t-lg">
              <h1 className="text-2xl md:text-3xl font-bold text-center mb-2">
                SCL-90症状自评量表
              </h1>
              <p className="text-blue-100 text-center text-sm md:text-base">
                国际通用心理健康筛查工具
              </p>
            </div>

            {/* 主要内容区域 */}
            <div className="p-6 space-y-6">
              {/* 快速介绍 */}
              <div className="text-center mb-6">
                <p className="text-gray-700 text-base md:text-lg leading-relaxed">
                  通过90道题目评估您近期的心理状态和情绪倾向，涵盖9个重要维度
                </p>
              </div>

              {/* 维度展示 - 响应式网格 */}
              {/* <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-center">
                <div className="p-2 bg-blue-50 rounded text-sm md:text-base">躯体化</div>
                <div className="p-2 bg-blue-50 rounded text-sm md:text-base">强迫症状</div>
                <div className="p-2 bg-blue-50 rounded text-sm md:text-base">人际关系敏感</div>
                <div className="p-2 bg-blue-50 rounded text-sm md:text-base">抑郁</div>
                <div className="p-2 bg-blue-50 rounded text-sm md:text-base">焦虑</div>
                <div className="p-2 bg-blue-50 rounded text-sm md:text-base">敌对</div>
                <div className="p-2 bg-blue-50 rounded text-sm md:text-base">恐怖</div>
                <div className="p-2 bg-blue-50 rounded text-sm md:text-base">偏执</div>
                <div className="p-2 bg-blue-50 rounded text-sm md:text-base">精神病性</div>
              </div> */}

              {/* 评分说明 */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-semibold text-gray-800 mb-3 text-center">评分标准</h3>
                <div className="grid grid-cols-5 gap-1 text-center text-xs md:text-sm">
                  <div className="p-2 bg-green-100 rounded">1=无</div>
                  <div className="p-2 bg-yellow-100 rounded">2=轻度</div>
                  <div className="p-2 bg-orange-100 rounded">3=中度</div>
                  <div className="p-2 bg-red-100 rounded">4=偏重</div>
                  <div className="p-2 bg-red-200 rounded">5=严重</div>
                </div>
                <p className="text-gray-500 text-xs md:text-sm text-center mt-2">
                  根据最近1周内的实际感受选择，无需过度思考
                </p>
                

              </div>
                {/* 开始测试按钮 */}
                <div className="mt-6 ml-6 mr-6 md:ml-10 md:mr-10">
                  <button
                    onClick={() => setShowInstructions(false)}
                    className="w-full bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white text-lg md:text-xl font-bold py-4 md:py-5 px-8 rounded-lg transition-all duration-200 transform hover:scale-105 shadow-lg focus:outline-none focus:ring-4 focus:ring-blue-300"
                  >
                    开始测试
                  </button>
                </div>
              {/* 重要提示 */}
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-yellow-800">
                      <strong>重要提示：</strong>本测试仅作为参考工具，不能替代专业诊断。
                    </p>
                  </div>
                </div>
              </div>

              {/* 时间预估 */}
              <div className="flex items-center justify-center text-gray-600 text-sm md:text-base">
                <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>预计耗时：15-20分钟（请确保环境安静）</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Progress type="circle" percent={100} status="active" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-4">
        <Card className="w-full max-w-md">
          <div className="text-center">
            {error === '测试不存在' ? (
              <h2 className="text-gray-600 mb-4">测试链接不存在，请检查链接，重新复制正确链接进行测试。如果问题依旧存在，请截图后联系管理员。</h2>
            ) : (
              <div><h2 className="text-xl font-bold mb-4">{error}</h2><p className="text-gray-700 mb-2">请截图后联系管理员反馈问题</p></div>
            )}
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <p className="text-blue-800 font-bold mb-2">当前链接：</p>
              <div className="flex items-center gap-2">
                <code className="bg-white px-2 py-1 rounded border text-xs break-all flex-1">
                  {window.location.href}
                </code>
                {/* <Button 
                  size="small" 
                  onClick={() => {
                    navigator.clipboard.writeText(window.location.href).then(() => {
                      setCopyStatus('链接已复制到剪贴板');
                      setTimeout(() => setCopyStatus(''), 2000);
                    }).catch(() => {
                      setCopyStatus('复制失败，请手动复制');
                      setTimeout(() => setCopyStatus(''), 3000);
                    });
                  }}
                  className="flex-shrink-0"
                >
                  复制
                </Button> */}
              </div>
            </div>
            {/* <Button type="primary" onClick={() => navigate('/')}>
              返回首页
            </Button> */}
          </div>
        </Card>
      </div>
    );
  }

  // 确保 currentQuestionIndex 在有效范围内
  if (currentQuestionIndex >= questions.length) {
    setCurrentQuestionIndex(questions.length - 1);
    return null; // 等待状态更新后重新渲染
  }

  const currentQuestion = questions[currentQuestionIndex];
  if (!currentQuestion) {
    return (
      <div style={{ textAlign: 'center', padding: '48px' }}>
        <Alert
          message="错误"
          description="题目数据加载异常，请刷新页面重试"
          type="error"
          showIcon
        />
      </div>
    );
  }
  const progress = ((currentQuestionIndex + 1) / questions.length) * 100;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div style={{ padding: '24px', maxWidth: '800px', margin: '0 auto' }}>
        <Card>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '48px 0' }}>
              <Spin size="large" />
              <div style={{ marginTop: '16px', color: '#666' }}>加载中...</div>
            </div>
          ) : error ? (
            <div style={{ textAlign: 'center', padding: '48px 0' }}>
              <Alert
                message="错误"
                description={error}
                type="error"
                showIcon
              />
              {/* <Button 
                type="primary" 
                onClick={() => navigate('/')}
                style={{ marginTop: '16px' }}
              >
                返回首页
              </Button> */}
            </div>
          ) : (
            <>
              <div style={{ marginBottom: '24px' }}>
                <Progress 
                  percent={Math.round((currentQuestionIndex + 1) / questions.length * 100)} 
                  showInfo={false}
                  strokeColor="#1890ff"
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px' }}>
                  <Text style={{ color: '#666' }}>问题 {currentQuestionIndex + 1} / {questions.length}</Text>
                  <div style={{ display: 'flex', gap: '16px' }}>
                    <Text style={{ color: '#52c41a', fontSize: '12px' }}>
                      已答题: {Object.keys(answers).length}
                    </Text>
                    <Text style={{ color: '#faad14', fontSize: '12px' }}>
                      未答题: {questions.length - Object.keys(answers).length}
                    </Text>
                  </div>
                </div>
              </div>

              <div style={{ position: 'relative', height: '400px', overflow: 'hidden', backgroundColor: '#fff', borderRadius: '8px' }}>
                <AnimatePresence mode="wait" initial={false}>
                  <motion.div
                    key={currentQuestionIndex}
                    initial={{ x: 20, scale: 0.95, opacity: 0 }}
                    animate={{ x: 0, scale: 1, opacity: 1 }}
                    exit={{ x: -20, scale: 0.95, opacity: 0 }}
                    transition={{ 
                      duration: 0.2,
                      ease: [0.4, 0.0, 0.2, 1]
                    }}
                    style={{ 
                      position: 'absolute',
                      width: '100%',
                      padding: '20px',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      willChange: 'transform, opacity',
                      boxSizing: 'border-box'
                    }}
                  >
                    <div style={{ marginBottom: '16px' }}>
                      <Title level={4} style={{ color: '#1a1a1a', marginBottom: '12px', fontSize: '16px' }}>
                        {currentQuestion.text}
                      </Title>
                    </div>

                    <div style={{ flex: 1, overflowY: 'auto', paddingRight: '4px' }}>
                      <Radio.Group 
                        onChange={(e: RadioChangeEvent) => handleAnswer(e.target.value)} 
                        value={answers[currentQuestion.id]}
                        style={{ width: '100%' }}
                      >
                        <Space direction="vertical" style={{ width: '100%' }}>
                          {[1, 2, 3, 4, 5].map(value => (
                            <Radio 
                              key={value} 
                              value={value}
                              disabled={isAnimating}
                              style={{ 
                                width: '100%',
                                padding: '8px 12px',
                                margin: '2px 0',
                                borderRadius: '4px',
                                backgroundColor: answers[currentQuestion.id] === value ? '#e6f7ff' : '#fff',
                                border: `1px solid ${answers[currentQuestion.id] === value ? '#1890ff' : '#d9d9d9'}`,
                                transition: 'all 0.3s ease',
                                display: 'flex',
                                alignItems: 'center',
                                height: '40px',
                                boxSizing: 'border-box',
                                opacity: isAnimating ? 0.6 : 1
                              }}
                            >
                              <Text style={{ fontSize: '14px', color: '#1a1a1a', margin: 0, lineHeight: '1.2' }}>
                                <span style={{ fontWeight: 'bold', marginRight: '4px' }}>{value}分</span>
                                <span style={{ color: '#666' }}>- {getScoreDescription(value)}</span>
                              </Text>
                            </Radio>
                          ))}
                        </Space>
                      </Radio.Group>
                    </div>
                  </motion.div>
                </AnimatePresence>
              </div>

              <div style={{ marginTop: '32px', display: 'flex', justifyContent: 'space-between' }}>
                <Button 
                  onClick={handlePrevious}
                  disabled={currentQuestionIndex === 0}
                  style={{ minWidth: '100px' }}
                >
                  上一题
                </Button>
                <Button 
                  type="primary"
                  onClick={() => {
                    if (currentQuestionIndex === questions.length - 1) {
                      handleSubmit();
                    } else {
                      handleNext();
                    }
                  }}
                  loading={isSubmitting}
                  disabled={answers[currentQuestion.id] === undefined || isAnimating || isSubmitting}
                  className={`min-w-[100px] ${answers[currentQuestion.id] !== undefined ? 'bg-blue-500 hover:bg-blue-600 border-blue-500' : ''}`}
                  style={{ minWidth: '100px' }}
                >
                  {currentQuestionIndex === questions.length - 1 ? '提交测试' : '下一题'}
                </Button>
              </div>
            </>
          )}
        </Card>
      </div>
    </motion.div>
  );
};

const getScoreDescription = (score: number): string => {
  switch (score) {
    case 1:
      return '完全没有';
    case 2:
      return '轻度';
    case 3:
      return '中度';
    case 4:
      return '偏重';
    case 5:
      return '严重';
    default:
      return '';
  }
};

export default Test; 
